(() => {
  if (customElements.get("module-explorer")) return;

  class ModuleExplorer extends HTMLElement {
    constructor() {
      super();
      this.cleanup = [];
    }

    connectedCallback() {
      this.moduleItems = Array.from(this.querySelectorAll("[data-module]"));
      this.modulePanels = Array.from(this.querySelectorAll("[data-module-panel]"));

      if (!this.moduleItems.length || !this.modulePanels.length) {
        return;
      }

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

      this.addListener(this, "click", this.onClick);
      this.addListener(this, "keydown", this.onKeydown);
      this.addListener(document, "keydown", this.onDocumentKeydown);

      const firstId = this.moduleItems[0].dataset.moduleId;
      if (firstId) {
        this.setActiveModule(firstId);
      }
    }

    disconnectedCallback() {
      this.cleanup.forEach((remove) => remove());
      this.cleanup = [];
    }

    addListener(el, event, handler, options) {
      if (!el) return;
      el.addEventListener(event, handler, options);
      this.cleanup.push(() => el.removeEventListener(event, handler, options));
    }

    onClick(event) {
      const closeButton = event.target.closest("[data-lesson-close]");
      if (closeButton && this.contains(closeButton)) {
        this.closeLesson(closeButton.closest("[data-module-panel]"));
        return;
      }

      const lessonCard = event.target.closest("[data-lesson-card]");
      if (lessonCard && this.contains(lessonCard)) {
        this.toggleLesson(lessonCard);
        return;
      }

      const openOverlay = event.target.closest("[data-lesson-overlay].is-open");
      if (openOverlay && this.contains(openOverlay)) {
        this.closeLesson(openOverlay.closest("[data-module-panel]"));
        return;
      }

      const moduleTarget = event.target.closest("[data-module]");
      if (moduleTarget && this.contains(moduleTarget)) {
        this.setActiveModule(moduleTarget.dataset.moduleId);
      }
    }

    onKeydown(event) {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      const target = event.target.closest(
        "[data-module], [data-lesson-card], [data-lesson-close]",
      );
      if (!target || !this.contains(target)) {
        return;
      }
      event.preventDefault();
      target.click();
    }

    onDocumentKeydown(event) {
      if (event.key !== "Escape") {
        return;
      }
      const activePanel = this.modulePanels.find((panel) =>
        panel.classList.contains("is-active"),
      );
      if (activePanel) {
        this.closeLesson(activePanel);
      }
    }

    setActiveModule(id) {
      if (!id || this.activeModuleId === id) {
        return;
      }
      this.activeModuleId = id;

      this.moduleItems.forEach((item) => {
        const isActive = item.dataset.moduleId === id;
        item.classList.toggle("is-active", isActive);
        item.setAttribute("aria-pressed", isActive ? "true" : "false");
      });

      this.modulePanels.forEach((panel) => {
        const isActive = panel.dataset.moduleId === id;
        panel.classList.toggle("is-active", isActive);
        this.closeLesson(panel);
        const video = panel.querySelector("video");
        if (!video) return;
        if (isActive) {
          video.currentTime = 0;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    toggleLesson(card) {
      const panel = card.closest("[data-module-panel]");
      if (!panel) return;
      const overlay = panel.querySelector("[data-lesson-overlay]");
      if (!overlay) return;

      const isSameLesson = overlay.dataset.lessonIndex === card.dataset.lessonIndex;
      const isOpen = overlay.classList.contains("is-open");

      if (isOpen && isSameLesson) {
        this.closeLesson(panel);
        return;
      }

      const titleEl = overlay.querySelector("[data-lesson-overlay-title]");
      const metaEl = overlay.querySelector("[data-lesson-overlay-meta]");
      const descriptionEl = overlay.querySelector("[data-lesson-overlay-description]");
      if (titleEl) titleEl.textContent = card.dataset.lessonTitle || "";
      if (metaEl) metaEl.textContent = card.dataset.lessonMeta || "";
      if (descriptionEl) descriptionEl.textContent = card.dataset.lessonDescription || "";
      overlay.dataset.lessonIndex = card.dataset.lessonIndex || "";

      panel.querySelectorAll("[data-lesson-card]").forEach((otherCard) => {
        otherCard.classList.toggle("is-active", otherCard === card);
      });

      overlay.classList.add("is-open");
      panel.classList.add("is-lesson-open");

      const body = overlay.querySelector("[data-lesson-overlay-body]");
      if (body) body.scrollTop = 0;
    }

    closeLesson(panel) {
      if (!panel) return;
      const overlay = panel.querySelector("[data-lesson-overlay]");
      if (overlay) {
        overlay.classList.remove("is-open");
        delete overlay.dataset.lessonIndex;
      }
      panel.classList.remove("is-lesson-open");
      panel.querySelectorAll("[data-lesson-card]").forEach((card) => {
        card.classList.remove("is-active");
      });
    }
  }

  customElements.define("module-explorer", ModuleExplorer);
})();
